// pages/Sales.jsx

import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    AppBar,
    Box,
    Button,
    Dialog,
    DialogContent,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Toolbar,
    Typography,
} from "@mui/material";

import AddIcon from
    "@mui/icons-material/Add";

import CloseIcon from
    "@mui/icons-material/Close";

import CloudDownloadIcon from
    "@mui/icons-material/CloudDownload";

import {
    LocalOffer,
} from "@material-ui/icons";

import {
    handleResponse,
} from "../util/integration";

import {
    POLLING_SPEED,
    usePolling,
} from "../util/polling";

import {
    fetchAuthoredLeadIdsAPI,
} from "../api/lead_api";

import {
    fetchUserCanSellAPI,
} from "../api/user_api";

import {
    PRIMARY_COLOR,
} from "../util/constants";

import Lead from
    "../entity/Lead";

import LeadPreview from
    "../fragments/LeadPreview";

import LeadCreationDialog from
    "../dialogs/LeadCreationDialog";


const LABEL = Object.freeze({
    title:
        "Verkäufe",

    addLead:
        "Lead hinzufügen",

    export:
        "Export",

    loadMore:
        "Weitere Leads anzeigen",

    createdAt:
        "Erstellt am",

    openLead:
        "Lead öffnen",

    closeDialog:
        "Dialog schließen",

    leadTitle: (
        leadId,
    ) => (
        `Lead #${leadId}`
    ),
});


const MESSAGE = Object.freeze({
    noSales:
        "Keine Ergebnisse",

    leadLoadError:
        "Der Lead konnte nicht geladen werden.",

    exportUnavailable:
        "Der CSV-Export wird später ergänzt.",
});


const CONFIG = Object.freeze({
    initialVisibleCount:
        12,

    loadMoreCount:
        12,

    topSpacing:
        "50px",

    headerPadding:
        "25px",

    pageSpacing:
        5,

    contentSpacing:
        7,

    fullWidthColumns:
        12,

    mediumColumns:
        6,

    largeColumns:
        4,

    dialogZIndex:
        6000,
});


const createStyles = (
    primaryColor,
) => Object.freeze({
    topSpacer: {
        height:
            CONFIG.topSpacing,
    },

    header: {
        p:
            CONFIG.headerPadding,

        backgroundColor:
            primaryColor,

        color:
            "white",

        display:
            "flex",

        alignItems:
            "center",

        gap:
            2,
    },

    headerTitleContainer: {
        display:
            "flex",

        alignItems:
            "center",

        gap:
            1.5,
    },

    headerIcon: {
        color:
            "white",

        fontSize:
            30,
    },

    headerActions: {
        ml:
            "auto",

        display:
            "flex",

        alignItems:
            "center",

        gap:
            2,
    },

    exportButton: {
        backgroundColor:
            "white",

        color:
            primaryColor,

        whiteSpace:
            "nowrap",

        "&:hover": {
            backgroundColor:
                "grey.100",
        },
    },

    addLeadButton: {
        backgroundColor:
            "white",

        color:
            primaryColor,

        whiteSpace:
            "nowrap",

        "&:hover": {
            backgroundColor:
                "grey.100",
        },

        "&.Mui-disabled": {
            backgroundColor:
                "grey.300",

            color:
                "grey.600",
        },
    },

    loadMoreContainer: {
        display:
            "flex",

        justifyContent:
            "center",

        mt:
            4,
    },

    dialog: {
        zIndex:
            CONFIG.dialogZIndex,
    },

    dialogAppBar: {
        backgroundColor:
            primaryColor,
    },

    dialogTitle: {
        ml:
            2,

        flex:
            1,
    },

    dialogContent: {
        backgroundColor:
            "background.default",

        margin:
            "75px",
    },
});


const STYLE =
    createStyles(
        PRIMARY_COLOR,
    );


const COMPONENT_PROPS = Object.freeze({
    pageGrid: {
        container:
            true,

        spacing:
            CONFIG.pageSpacing,
    },

    contentGrid: {
        container:
            true,

        spacing:
            CONFIG.contentSpacing,
    },

    fullWidthGridItem: {
        item:
            true,

        xs:
            CONFIG.fullWidthColumns,
    },

    leadGridItem: {
        item:
            true,

        xs:
            CONFIG.fullWidthColumns,

        sm:
            CONFIG.mediumColumns,

        lg:
            CONFIG.largeColumns,
    },

    title: {
        variant:
            "h6",

        component:
            "h1",
    },

    headerButton: {
        variant:
            "contained",
    },

    loadMoreButton: {
        variant:
            "outlined",
    },

    leadDialog: {
        fullScreen:
            true,
    },

    dialogAppBar: {
        position:
            "relative",
    },

    closeDialogButton: {
        edge:
            "start",

        color:
            "inherit",

        "aria-label":
            LABEL.closeDialog,
    },

    dialogTitle: {
        variant:
            "h6",

        component:
            "div",
    },

    lead: {
        seamless:
            true,
    },
});


const EXPORT_ICON = (
    <CloudDownloadIcon />
);


const ADD_LEAD_ICON = (
    <AddIcon />
);


const getCreatedDate = (
    lead,
) => {
    return (
        lead.creationTimestamp
        ?? lead.createdTimestamp
        ?? lead.createdAt
    );
};


const normalizeLeadIds = (
    response,
) => {
    if (!Array.isArray(response)) {
        return [];
    }

    const ids =
        response
            .map(
                (lead) => {
                    if (
                        typeof lead
                            === "object"
                        && lead !== null
                    ) {
                        return (
                            lead.id
                            ?? lead.leadId
                        );
                    }

                    return lead;
                },
            )
            .filter(
                (id) => (
                    id !== null
                    && id !== undefined
                ),
            );

    return [
        ...new Set(
            ids,
        ),
    ];
};


const leadIdsAreEqual = (
    currentIds,
    newIds,
) => {
    if (
        currentIds.length
        !== newIds.length
    ) {
        return false;
    }

    return currentIds.every(
        (
            id,
            index,
        ) => (
            id === newIds[index]
        ),
    );
};


const normalizeCanSell = (
    response,
) => {
    if (
        typeof response === "object"
        && response !== null
    ) {
        return Boolean(
            response.canSell
            ?? response.allowed
            ?? response.value,
        );
    }

    return Boolean(
        response,
    );
};


const useSalesPermission = ({
    password,
    showMessage,
    userId,
    username,
}) => {
    const [
        canSell,
        setCanSell,
    ] = useState(false);

    const [
        isPermissionLoading,
        setIsPermissionLoading,
    ] = useState(true);


    useEffect(() => {
        let isActive =
            true;

        setIsPermissionLoading(
            true,
        );


        const handleSuccess = (
            response,
        ) => {
            if (!isActive) {
                return;
            }

            setCanSell(
                normalizeCanSell(
                    response,
                ),
            );

            setIsPermissionLoading(
                false,
            );
        };


        const handleFailure = (
            message,
        ) => {
            if (!isActive) {
                return;
            }

            setCanSell(
                false,
            );

            setIsPermissionLoading(
                false,
            );

            showMessage(
                message,
            );
        };


        handleResponse(
            fetchUserCanSellAPI(
                userId,
                username,
                password,
            ),
            handleSuccess,
            handleFailure,
        );


        return () => {
            isActive =
                false;
        };
    }, [
        password,
        showMessage,
        userId,
        username,
    ]);


    return {
        canSell,

        isPermissionLoading,
    };
};


const useSalesPolling = ({
    password,
    showMessage,
    userId,
}) => {
    const [
        createdLeadIds,
        setCreatedLeadIds,
    ] = useState([]);

    const [
        isInitialLoading,
        setIsInitialLoading,
    ] = useState(true);

    const [
        reloadVersion,
        setReloadVersion,
    ] = useState(0);


    const hasUserId = (
        userId !== null
        && userId !== undefined
    );


    useEffect(() => {
        setCreatedLeadIds(
            [],
        );

        setIsInitialLoading(
            hasUserId,
        );
    }, [
        hasUserId,
        userId,
    ]);


    const pollingRestartKey =
        useMemo(
            () => ({
                password,
                reloadVersion,
                userId,
            }),
            [
                password,
                reloadVersion,
                userId,
            ],
        );


    const pollCreatedLeads =
        useCallback(
            ({
                isPollingActive,
            }) => {
                if (!hasUserId) {
                    return Promise.resolve();
                }

                return new Promise(
                    (
                        resolve,
                        reject,
                    ) => {
                        const handleSuccess = (
                            response,
                        ) => {
                            if (
                                !isPollingActive()
                            ) {
                                resolve();
                                return;
                            }

                            try {
                                const normalizedIds =
                                    normalizeLeadIds(
                                        response,
                                    );

                                setCreatedLeadIds(
                                    (
                                        currentIds,
                                    ) => {
                                        if (
                                            leadIdsAreEqual(
                                                currentIds,
                                                normalizedIds,
                                            )
                                        ) {
                                            return currentIds;
                                        }

                                        return normalizedIds;
                                    },
                                );

                                setIsInitialLoading(
                                    false,
                                );

                                resolve();
                            } catch (error) {
                                reject(
                                    error,
                                );
                            }
                        };


                        const handleFailure = (
                            message,
                        ) => {
                            if (
                                !isPollingActive()
                            ) {
                                resolve();
                                return;
                            }

                            try {
                                setIsInitialLoading(
                                    false,
                                );

                                showMessage(
                                    message,
                                );

                                resolve();
                            } catch (error) {
                                reject(
                                    error,
                                );
                            }
                        };


                        try {
                            handleResponse(
                                fetchAuthoredLeadIdsAPI(
                                    password,
                                    userId,
                                ),
                                handleSuccess,
                                handleFailure,
                            );
                        } catch (error) {
                            reject(
                                error,
                            );
                        }
                    },
                );
            },
            [
                hasUserId,
                password,
                showMessage,
                userId,
            ],
        );


    const handlePollingError =
        useCallback(
            () => {
                setIsInitialLoading(
                    false,
                );
            },
            [],
        );


    usePolling({
        poll:
            pollCreatedLeads,

        enabled:
            hasUserId,

        immediate:
            true,

        speed:
            POLLING_SPEED.medium,

        onError:
            handlePollingError,

        restartKey:
            pollingRestartKey,
    });


    const reloadCreatedLeads =
        useCallback(
            () => {
                setReloadVersion(
                    (
                        currentVersion,
                    ) => (
                        currentVersion + 1
                    ),
                );
            },
            [],
        );


    return {
        createdLeadIds,

        hasCreatedLeads:
            createdLeadIds.length > 0,

        isInitialLoading,

        reloadCreatedLeads,
    };
};


const useSalesPagination = ({
    createdLeadIds,
    userId,
}) => {
    const [
        visibleCount,
        setVisibleCount,
    ] = useState(
        CONFIG.initialVisibleCount,
    );


    useEffect(() => {
        setVisibleCount(
            CONFIG.initialVisibleCount,
        );
    }, [
        userId,
    ]);


    const visibleLeadIds =
        useMemo(
            () => (
                createdLeadIds.slice(
                    0,
                    visibleCount,
                )
            ),
            [
                createdLeadIds,
                visibleCount,
            ],
        );


    const canLoadMore = (
        visibleCount
        < createdLeadIds.length
    );


    const showMoreLeads =
        useCallback(
            () => {
                setVisibleCount(
                    (
                        currentCount,
                    ) => (
                        currentCount
                        + CONFIG.loadMoreCount
                    ),
                );
            },
            [],
        );


    return {
        canLoadMore,

        showMoreLeads,

        visibleLeadIds,
    };
};


const useLeadDialog = ({
    userId,
}) => {
    const [
        selectedLeadId,
        setSelectedLeadId,
    ] = useState(null);


    useEffect(() => {
        setSelectedLeadId(
            null,
        );
    }, [
        userId,
    ]);


    const openLead =
        useCallback(
            (
                leadId,
            ) => {
                setSelectedLeadId(
                    leadId,
                );
            },
            [],
        );


    const closeLead =
        useCallback(
            () => {
                setSelectedLeadId(
                    null,
                );
            },
            [],
        );


    const hasSelectedLead =
        selectedLeadId !== null;


    return {
        closeLead,

        hasSelectedLead,

        openLead,

        selectedLeadId,

        selectedLeadTitle:
            hasSelectedLead
                ? LABEL.leadTitle(
                    selectedLeadId,
                )
                : null,
    };
};


const useAddLeadDialog = () => {
    const [
        isAddLeadOpen,
        setIsAddLeadOpen,
    ] = useState(false);


    const openAddLead =
        useCallback(
            () => {
                setIsAddLeadOpen(
                    true,
                );
            },
            [],
        );


    const setAddLeadOpen =
        useCallback(
            (
                isOpen,
            ) => {
                setIsAddLeadOpen(
                    isOpen,
                );
            },
            [],
        );


    return {
        isAddLeadOpen,

        openAddLead,

        setAddLeadOpen,
    };
};


const useSales = ({
    password,
    showMessage,
    userId,
    username,
}) => {
    const {
        canSell,
        isPermissionLoading,
    } = useSalesPermission({
        password,
        showMessage,
        userId,
        username,
    });


    const {
        createdLeadIds,
        hasCreatedLeads,
        isInitialLoading,
        reloadCreatedLeads,
    } = useSalesPolling({
        password,
        showMessage,
        userId,
    });


    const {
        canLoadMore,
        showMoreLeads,
        visibleLeadIds,
    } = useSalesPagination({
        createdLeadIds,
        userId,
    });


    const {
        closeLead,
        hasSelectedLead,
        openLead,
        selectedLeadId,
        selectedLeadTitle,
    } = useLeadDialog({
        userId,
    });


    const {
        isAddLeadOpen,
        openAddLead,
        setAddLeadOpen,
    } = useAddLeadDialog();


    const downloadSales =
        useCallback(
            () => {
                showMessage(
                    MESSAGE.exportUnavailable,
                );
            },
            [
                showMessage,
            ],
        );


    return {
        canLoadMore,

        closeLead,

        downloadSales,

        hasCreatedLeads,

        hasSelectedLead,

        isAddLeadDisabled: (
            isPermissionLoading
            || !canSell
        ),

        isAddLeadOpen,

        isInitialLoading,

        openAddLead,

        openLead,

        reloadCreatedLeads,

        selectedLeadId,

        selectedLeadTitle,

        setAddLeadOpen,

        showMoreLeads,

        visibleLeadIds,
    };
};


function SalesHeaderActions({
    downloadSales,
    isAddLeadDisabled,
    openAddLead,
}) {
    return (
        <Box
            sx={STYLE.headerActions}
        >
            <Button
                {...COMPONENT_PROPS.headerButton}
                onClick={downloadSales}
                startIcon={EXPORT_ICON}
                sx={STYLE.exportButton}
            >
                {LABEL.export}
            </Button>

            <Button
                {...COMPONENT_PROPS.headerButton}
                disabled={isAddLeadDisabled}
                startIcon={ADD_LEAD_ICON}
                onClick={openAddLead}
                sx={STYLE.addLeadButton}
            >
                {LABEL.addLead}
            </Button>
        </Box>
    );
}


function SalesLeadPreview({
    appAPI,
    contextAPI,
    leadId,
    openLead,
}) {
    const handleOpenLead =
        useCallback(
            () => {
                openLead(
                    leadId,
                );
            },
            [
                leadId,
                openLead,
            ],
        );


    return (
        <LeadPreview
            leadId={leadId}
            appAPI={appAPI}
            contextAPI={contextAPI}
            dateLabel={LABEL.createdAt}
            getDateValue={getCreatedDate}
            actionLabel={LABEL.openLead}
            errorMessage={MESSAGE.leadLoadError}
            onAction={handleOpenLead}
        />
    );
}


function SalesLeadDialog({
    appAPI,
    closeLead,
    contextAPI,
    hasSelectedLead,
    selectedLeadId,
    selectedLeadTitle,
}) {
    return (
        <Dialog
            {...COMPONENT_PROPS.leadDialog}
            open={hasSelectedLead}
            onClose={closeLead}
            sx={STYLE.dialog}
        >
            <AppBar
                {...COMPONENT_PROPS.dialogAppBar}
                sx={STYLE.dialogAppBar}
            >
                <Toolbar>
                    <IconButton
                        {...COMPONENT_PROPS.closeDialogButton}
                        onClick={closeLead}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography
                        {...COMPONENT_PROPS.dialogTitle}
                        sx={STYLE.dialogTitle}
                    >
                        {selectedLeadTitle}
                    </Typography>
                </Toolbar>
            </AppBar>

            <DialogContent
                sx={STYLE.dialogContent}
            >
                {hasSelectedLead && (
                    <Lead
                        {...COMPONENT_PROPS.lead}
                        key={selectedLeadId}
                        appAPI={appAPI}
                        contextAPI={contextAPI}
                        leadId={selectedLeadId}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}


export default function Sales({
    appAPI,
    contextAPI,
}) {
    const userId =
        contextAPI.id;

    const username =
        contextAPI.username;

    const password =
        contextAPI.password;

    const showMessage =
        appAPI.showMessage;


    const {
        canLoadMore,
        closeLead,
        downloadSales,
        hasCreatedLeads,
        hasSelectedLead,
        isAddLeadDisabled,
        isAddLeadOpen,
        isInitialLoading,
        openAddLead,
        openLead,
        reloadCreatedLeads,
        selectedLeadId,
        selectedLeadTitle,
        setAddLeadOpen,
        showMoreLeads,
        visibleLeadIds,
    } = useSales({
        password,
        showMessage,
        userId,
        username,
    });


    return (
        <>
            <Grid
                {...COMPONENT_PROPS.pageGrid}
            >
                <Grid
                    {...COMPONENT_PROPS.fullWidthGridItem}
                >
                    <Box
                        sx={STYLE.topSpacer}
                    />
                </Grid>

                <Grid
                    {...COMPONENT_PROPS.fullWidthGridItem}
                >
                    <Grid
                        {...COMPONENT_PROPS.contentGrid}
                    >
                        <Grid
                            {...COMPONENT_PROPS.fullWidthGridItem}
                        >
                            <Paper
                                sx={STYLE.header}
                            >
                                <Box
                                    sx={
                                        STYLE
                                            .headerTitleContainer
                                    }
                                >
                                    <LocalOffer
                                        sx={STYLE.headerIcon}
                                    />

                                    <Typography
                                        {...COMPONENT_PROPS.title}
                                    >
                                        {LABEL.title}
                                    </Typography>
                                </Box>

                                <SalesHeaderActions
                                    downloadSales={
                                        downloadSales
                                    }
                                    isAddLeadDisabled={
                                        isAddLeadDisabled
                                    }
                                    openAddLead={
                                        openAddLead
                                    }
                                />
                            </Paper>
                        </Grid>

                        {isInitialLoading && (
                            <Grid
                                {...COMPONENT_PROPS.fullWidthGridItem}
                            >
                                <LinearProgress />
                            </Grid>
                        )}

                        {!isInitialLoading
                            && !hasCreatedLeads
                            && (
                                <Grid
                                    {...COMPONENT_PROPS.fullWidthGridItem}
                                >
                                    <Typography>
                                        {MESSAGE.noSales}
                                    </Typography>
                                </Grid>
                            )}

                        {!isInitialLoading
                            && visibleLeadIds.map(
                                (
                                    leadId,
                                ) => (
                                    <Grid
                                        {...COMPONENT_PROPS.leadGridItem}
                                        key={leadId}
                                    >
                                        <SalesLeadPreview
                                            appAPI={appAPI}
                                            contextAPI={contextAPI}
                                            leadId={leadId}
                                            openLead={openLead}
                                        />
                                    </Grid>
                                ),
                            )}
                    </Grid>

                    {canLoadMore && (
                        <Box
                            sx={STYLE.loadMoreContainer}
                        >
                            <Button
                                {...COMPONENT_PROPS.loadMoreButton}
                                onClick={showMoreLeads}
                            >
                                {LABEL.loadMore}
                            </Button>
                        </Box>
                    )}
                </Grid>
            </Grid>

            <SalesLeadDialog
                appAPI={appAPI}
                closeLead={closeLead}
                contextAPI={contextAPI}
                hasSelectedLead={hasSelectedLead}
                selectedLeadId={selectedLeadId}
                selectedLeadTitle={
                    selectedLeadTitle
                }
            />

            <LeadCreationDialog
                appAPI={appAPI}
                contextAPI={contextAPI}
                open={isAddLeadOpen}
                setOpen={setAddLeadOpen}
                reload={reloadCreatedLeads}
            />
        </>
    );
}